import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { submitContact } from "../../api/contact";

const EmailAlertForm: React.FC = () => {
  const location = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [criteria, setCriteria] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSubmitting(true);

    try {
      await submitContact({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        subject: "Property search alert",
        message: criteria.trim(),
        sourcePage: location.pathname + location.search,
        enquiryType: "alert",
      });

      setSuccess(true);
      setName("");
      setEmail("");
      setPhone("");
      setCriteria("");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create your alert"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-bg-porcelain border-t border-solid border-[rgba(0,0,0,0.06)]">
      <div className="max-w-[900px] mx-auto px-[10%]">
        <div className="mb-8 pl-6 border-l-4 border-l-solid border-l-brand-yellow">
          <span className="font-manrope text-xs uppercase tracking-wider text-text-slate block mb-2">
            Stay informed
          </span>
          <h2 className="font-teko text-4xl text-text-obsidian">
            Email me matching properties
          </h2>
          <p className="font-manrope text-sm text-text-slate mt-3 max-w-xl">
            Tell us your requirements and we will email you when new listings
            match your search.
          </p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="grid gap-4 bg-white p-8 border border-solid border-[rgba(0,0,0,0.06)] shadow-soft"
        >
          {success && (
            <p className="font-manrope text-sm text-green-700 bg-green-50 p-4 border border-solid border-green-200">
              Thank you — your alert has been set up.
            </p>
          )}
          {error && (
            <p className="font-manrope text-sm text-red-700 bg-red-50 p-4 border border-solid border-red-200">
              {error}
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <input
              type="text"
              className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope focus:outline-none focus:border-brand-yellow"
              placeholder="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={submitting}
            />
            <input
              type="email"
              className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope focus:outline-none focus:border-brand-yellow"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
            />
          </div>
          <input
            type="tel"
            className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope focus:outline-none focus:border-brand-yellow"
            placeholder="Phone (optional)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={submitting}
          />
          <textarea
            className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope focus:outline-none focus:border-brand-yellow"
            rows={4}
            placeholder="e.g. 2 bed flat in Mayfair, max £4,000 PCM, furnished"
            value={criteria}
            onChange={(e) => setCriteria(e.target.value)}
            required
            disabled={submitting}
          />
          <button
            type="submit"
            className="w-full sm:w-auto px-8 py-4 bg-text-obsidian text-white font-teko text-xl uppercase tracking-wider hover:bg-brand-yellow hover:text-black transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={submitting}
          >
            {submitting ? "Submitting…" : "Create alert"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default EmailAlertForm;
